import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { adminApi, Card, StatTile, Badge, inpCls, btnPrimary } from './_shared';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminLicense() {
  const [lic, setLic] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    try {
      const [l, t] = await Promise.all([adminApi.license(), adminApi.tenants()]);
      setLic(l); setTenants(t.tenants || []);
    } catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = () => setEdit({
    tier: lic.tier, seat_limit: lic.seats.limit, project_limit: lic.projects.limit,
    expires_at: lic.expires_at, support_level: lic.support_level,
  });
  const save = async () => {
    try { await adminApi.updateLicense(edit); toast.success('License updated'); setEdit(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (!lic) return (
    <AdminLayout title="License & Tenants" subtitle="Admin · Subscription">
      <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-500"/></div>
    </AdminLayout>
  );

  const seatPct = Math.round((lic.seats.used / lic.seats.limit) * 100);
  const projectPct = Math.round((lic.projects.used / lic.projects.limit) * 100);
  const seatTone = seatPct > 90 ? 'red' : seatPct > 70 ? 'amber' : 'green';
  const projTone = projectPct > 90 ? 'red' : projectPct > 70 ? 'amber' : 'green';

  return (
    <AdminLayout title="License & Tenants" subtitle="Admin · Subscription"
      actions={!edit && <button onClick={startEdit} className={btnPrimary} data-testid="lic-edit-btn">Edit License</button>}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="license-stats">
        <StatTile label="Tier"             value={lic.tier} sub="Active" accent="orange"/>
        <StatTile label="Seats"            value={`${lic.seats.used}/${lic.seats.limit}`}       sub={`${lic.seats.available} available`} accent={seatTone === 'green' ? 'green' : seatTone === 'amber' ? 'orange' : 'red'}/>
        <StatTile label="Projects"         value={`${lic.projects.used}/${lic.projects.limit}`} sub={`${lic.projects.available} available`} accent={projTone === 'green' ? 'green' : projTone === 'amber' ? 'orange' : 'red'}/>
        <StatTile label="Expiry"           value={lic.expires_at} sub={lic.support_level} accent="blue"/>
      </div>

      {edit && (
        <Card className="p-6 mb-8">
          <h3 className="text-sm tracking-[0.15em] uppercase text-slate-400 mb-4">Edit License</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {[['tier','Tier','text'],['seat_limit','Seat Limit','number'],['project_limit','Project Limit','number'],['expires_at','Expiry (YYYY-MM-DD)','text'],['support_level','Support Level','text']].map(([k,l,t]) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{l}</label>
                <input type={t} className={`${inpCls} w-full mt-1`} value={edit[k] || ''} onChange={e => setEdit(p => ({...p, [k]: t === 'number' ? Number(e.target.value) : e.target.value}))}/>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-5">
            <button onClick={save} className={btnPrimary} data-testid="lic-save"><Save className="w-3.5 h-3.5"/>Save</button>
            <button onClick={() => setEdit(null)} className="text-xs text-slate-500 hover:text-slate-100 px-3 py-2">Cancel</button>
          </div>
        </Card>
      )}

      <h3 className="text-sm tracking-[0.15em] uppercase text-slate-400 mb-4">Tenants</h3>
      <div className="space-y-2" data-testid="tenants-list">
        {tenants.map(t => (
          <Card key={t.tenant_id} className="px-5 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm text-slate-100">{t.name}</div>
              <div className="text-[11px] text-slate-500 font-mono mt-0.5">{t.tenant_id}</div>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-400">
              <span>{t.users} users</span>
              <span>{t.projects} projects</span>
              <Badge tone="slate">since {(t.created_at || '').slice(0, 10)}</Badge>
            </div>
          </Card>
        ))}
      </div>
    </AdminLayout>
  );
}
